import { Descriptions, Divider, Grid } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import * as permissionKind from '../../../../../shared/permissions';
// eslint-disable-next-line max-len
import { type AdyenPaymentModuleWithPermissionsSpecsFragment } from '../../../../api/fragments/AdyenPaymentModuleWithPermissionsSpecs';
import ConsolePageWithHeader from '../../../../layouts/ConsoleLayout/ConsolePageWithHeader';
import hasPermissions from '../../../../utilities/hasPermissions';
import useFormattedSimpleVersioning from '../../../../utilities/useFormattedSimpleVersioning';
import AdyenPaymentModuleActions from './AdyenPaymentModuleActions';
import AdyenPaymentSettingsEmptyList from './AdyenPaymentSettingsEmptyList';
import AdyenPaymentSettingsTable from './AdyenPaymentSettingsTable';

export type AdyenPaymentModulePageProps = { module: AdyenPaymentModuleWithPermissionsSpecsFragment };

const AdyenPaymentModulePage = ({ module }: AdyenPaymentModulePageProps) => {
    const { t } = useTranslation(['adyenPaymentModuleDetails']);
    const navigate = useNavigate();
    const { updated, offset } = useFormattedSimpleVersioning({
        versioning: module.versioning,
        timeZone: module.company.timeZone,
    });
    const screens = Grid.useBreakpoint();

    const permissions = {
        hasUpdatePermission: hasPermissions(module.permissions, [permissionKind.updateModule]),
        hasDeletePermission: hasPermissions(module.permissions, [permissionKind.deleteModule]),
    };

    const content = (
        <Descriptions column={{ xs: 1, md: 2 }} bordered>
            <Descriptions.Item label={t('adyenPaymentModuleDetails:content.displayName')}>
                {module.displayName}
            </Descriptions.Item>
            <Descriptions.Item label={t('adyenPaymentModuleDetails:content.company')}>
                {module.company.displayName}
            </Descriptions.Item>
            <Descriptions.Item {...t('common:fields.updated', { returnObjects: true, offset })}>
                {updated}
            </Descriptions.Item>
        </Descriptions>
    );

    return (
        <ConsolePageWithHeader
            content={content}
            extra={<AdyenPaymentModuleActions module={module} permissions={permissions} />}
            onBack={() => navigate('/admin/system/modules')}
            title={screens.md ? t('adyenPaymentModuleDetails:title', { name: module.displayName }) : module.displayName}
        >
            <Divider>{t('adyenPaymentModuleDetails:dividers.mainDetails')}</Divider>
            {module.paymentSettings.length === 0 ? (
                <AdyenPaymentSettingsEmptyList module={module} />
            ) : (
                <AdyenPaymentSettingsTable module={module} />
            )}
        </ConsolePageWithHeader>
    );
};

export default AdyenPaymentModulePage;
